import React, { useEffect } from 'react';
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  Typography,
  Grid,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import BlockIcon from '@material-ui/icons/Block';
import { FixAction, AnalysisJobStatus, Requirement } from './types';
import * as api from './api/api';
import { useSharedStyles } from './styles/shared';

interface ApplyingFixesScreenProps {
  requirement: Requirement;
  selectedActions: FixAction[];
  onCancel: () => void;
  onComplete: (
    fixedText?: string,
    notes?: string,
    skipped?: api.FixSkippedSuggestion[],
  ) => void;
}

interface FixJob {
  id: string;
  action: FixAction;
  step: string;
  status: AnalysisJobStatus;
  progress: number;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

// statusIconMap is defined inside the component — see below.

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
  },
  minWidth60: {
    minWidth: 60,
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  bold: {
    fontWeight: 'bold',
  },
  progressCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  progressBar: {
    width: 100,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  cancelButton: {
    borderColor: theme.palette.error.main,
  },
  iconSuccess: {
    color: theme.palette.success.main,
  },
  iconError: {
    color: theme.palette.error.main,
  },
}));

const ApplyingFixesScreen: React.FC<ApplyingFixesScreenProps> = ({
  requirement,
  selectedActions,
  onCancel,
  onComplete,
}) => {
  const shared = useSharedStyles();
  const classes = useStyles();
  const statusIconMap: Record<AnalysisJobStatus, React.ReactNode> = {
    pending: <CircularProgress size={20} />,
    running: <CircularProgress size={20} />,
    completed: <CheckCircleIcon className={classes.iconSuccess} />,
    failed: <ErrorIcon className={classes.iconError} />,
    cancelled: <BlockIcon color="disabled" />,
  };
  const { t } = useTranslation('requirement-doctor');
  const [jobs, setJobs] = React.useState<FixJob[]>([]);
  const [startTime] = React.useState(new Date());
  const [apiError, setApiError] = React.useState<string | null>(null);

  // Call Backend API once on mount to apply fixes.
  useEffect(() => {
    const initialJobs = selectedActions.map((action, idx) => ({
      id: `fix-${idx}`,
      action,
      step: `Applying ${action.label}...`,
      status: 'running' as AnalysisJobStatus,
      progress: 50,
      startedAt: new Date().toISOString(),
    }));
    setJobs(initialJobs);

    const applyFix = async () => {
      if (
        requirement.threadId === undefined ||
        requirement.versionId === undefined
      ) {
        setApiError(
          'Missing backend identifiers for this requirement; cannot apply fixes.',
        );
        setJobs(prev =>
          prev.map(j => ({
            ...j,
            status: 'failed' as AnalysisJobStatus,
            progress: 0,
          })),
        );
        return;
      }
      try {
        const actionIds = api.toFixActionIds(selectedActions.map(a => a.id));
        const response = await api.fixRequirement(
          requirement.threadId,
          requirement.versionId,
          actionIds,
        );
        setJobs(prev =>
          prev.map(j => ({
            ...j,
            status: 'completed' as AnalysisJobStatus,
            progress: 100,
            step: `${j.action.label} completed`,
            completedAt: new Date().toISOString(),
          })),
        );
        onComplete(response.afterText, response.notes, response.skipped);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to apply fixes';
        setApiError(message);
        setJobs(prev =>
          prev.map(j => ({ ...j, status: 'failed' as AnalysisJobStatus })),
        );
      }
    };

    applyFix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedCount = jobs.filter(j => j.status === 'completed').length;
  const totalCount = jobs.length;
  const overallProgress =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isRunning = jobs.some(
    j => j.status === 'pending' || j.status === 'running',
  );

  const getActionLabel = (actionId: string): string => {
    switch (actionId) {
      case 'spell':
        return t('s6.actions.spell');
      case 'split':
        return t('s6.actions.split');
      case 'testability':
        return t('s6.actions.testability');
      case 'comprehensive':
        return t('s6.actions.comprehensive');
      default:
        return actionId;
    }
  };

  return (
    <Box className={shared.root}>
      <Typography variant="h6" className={shared.mb2}>
        {t('s6.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" className={shared.mb3}>
        {t('s6.description')}
      </Typography>
      {apiError && (
        <Alert severity="error" className={shared.mb2}>
          {apiError}
        </Alert>
      )}

      <Grid container spacing={2} className={shared.mb3}>
        <Grid item xs={12} sm={6}>
          <Paper className={classes.paper}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={shared.mb1}
            >
              {t('s6.overallProgress')}
            </Typography>
            <Box className={shared.progressRow}>
              <Box className={shared.flex1}>
                <LinearProgress variant="determinate" value={overallProgress} />
              </Box>
              <Typography variant="body2" className={classes.minWidth60}>
                {completedCount}/{totalCount}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper className={classes.paper}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={shared.mb1}
            >
              {t('s6.sessionInfo')}
            </Typography>
            <Box className={classes.chipRow}>
              <Chip
                size="small"
                label={t('s6.statusChips.statusLabel', {
                  status: isRunning
                    ? t('s6.statusChips.running')
                    : t('s6.statusChips.completed'),
                })}
                color={isRunning ? 'primary' : 'default'}
                className={isRunning ? undefined : shared.chipSuccess}
                variant="outlined"
              />
              <Chip
                size="small"
                label={t('s6.messages.started', {
                  time: startTime.toLocaleTimeString(),
                })}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} className={shared.mb2}>
        <Table size="small">
          <TableHead className={shared.tableHead}>
            <TableRow>
              <TableCell className={classes.bold}>
                {t('s6.table.columns.requirement')}
              </TableCell>
              <TableCell className={classes.bold}>
                {t('s6.table.columns.action')}
              </TableCell>
              <TableCell className={classes.bold} align="center">
                {t('s6.table.columns.progress')}
              </TableCell>
              <TableCell className={classes.bold} align="center">
                {t('s6.table.columns.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map(job => {
              const chipColor =
                job.status === 'running' ? 'primary' : 'default';
              const chipClassByStatus: Partial<
                Record<AnalysisJobStatus, string>
              > = {
                completed: shared.chipSuccess,
                failed: shared.chipError,
              };
              const chipClass = chipClassByStatus[job.status];
              return (
                <TableRow key={job.id} hover>
                  <TableCell>{requirement.id}</TableCell>
                  <TableCell>{getActionLabel(job.action.id)}</TableCell>
                  <TableCell align="center">
                    <Box className={classes.progressCell}>
                      <LinearProgress
                        variant="determinate"
                        value={job.progress}
                        className={classes.progressBar}
                      />
                      <Typography variant="caption">
                        {Math.round(job.progress)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.statusCell}>
                      {statusIconMap[job.status]}
                      <Chip
                        size="small"
                        label={job.status}
                        color={chipColor}
                        className={chipClass}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          className={`${shared.cancelButton} ${classes.cancelButton}`}
          startIcon={<CancelIcon />}
          onClick={onCancel}
          // Enabled while the fix is running (cancel it) and after it failed
          // (leave the screen). Keying this on isRunning alone meant a failed
          // fix disabled the only control on the screen: the jobs all went to
          // 'failed', isRunning went false, and the user was left on a dead
          // screen with no way back to the selection.
          disabled={!isRunning && !apiError}
        >
          {t('s6.buttons.cancel')}
        </Button>
      </Box>
    </Box>
  );
};

export default ApplyingFixesScreen;
